import * as React from 'react';

// PACKAGES
import { Formik, Field, ErrorMessage, Form } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import SingleSelect from '../../../components/SingleSelect';
import FileUpload from '../../../components/FileUpload';
import AddLaterRadio from '../../../components/AddLaterRadio';
import Button from '../../../components/Button';
import DateInput from '../../../components/DateInput';
import ExistingCertification from '../../../components/ExistingCertification';

// TYPES
import { CertificationTypes } from './CertificationStaff';
import { FileType } from '../../../components/FileUpload';
import { AccountExistingCertificationsCertification } from '../../../api/models';

interface PartThreeTypes {
	certificationData: CertificationTypes;
	setFormSection: React.Dispatch<React.SetStateAction<string>>;
	setCertificationFormData: React.Dispatch<React.SetStateAction<CertificationTypes>>;
	existingCertifications: AccountExistingCertificationsCertification[];
}

interface policeCheck {
	certificationType?: string;
	date?: string;
	createAttachments?: string[];
	attachments?: FileType[];
	addLater?: boolean;
}

const PartThree: React.FC<PartThreeTypes> = ({
	certificationData,
	setFormSection,
	setCertificationFormData,
	existingCertifications
}) => {
	const onboardingDocs = [{ label: 'Police Check', value: 'PoliceCheck' }];

	// Set Initial Values
	const PartThreeInitialValues: policeCheck = {
		certificationType: (certificationData.policeCheck && certificationData.policeCheck.certificationType) || '',
		date: (certificationData.policeCheck && certificationData.policeCheck.date) || '',
		attachments:
			certificationData && certificationData.policeCheck?.attachments ? certificationData.policeCheck.attachments : [],
		createAttachments:
			certificationData && certificationData.policeCheck?.createAttachments
				? certificationData.policeCheck.createAttachments
				: [],
		addLater:
			certificationData && certificationData.policeCheck?.certificationType
				? false
				: existingCertifications &&
				  existingCertifications.length > 0 &&
				  existingCertifications.filter((item) => item.validCertification).length > 0
				? true
				: false
	};

	//Submit form
	const continueFormStepTwo = (values: any) => {
		setFormSection('4');
		if (values.addLater) {
			setCertificationFormData((prevState) => ({
				...prevState,
				policeCheck: {
					certificationType: '',
					date: '',
					createAttachments: []
				}
			}));
		}
	};

	// Validation
	const formTwoValidationSchema = Yup.object().shape({
		certificationType: Yup.string().when('addLater', { is: false, then: Yup.string().required('Required') }),
		date: Yup.string().when('addLater', { is: false, then: Yup.string().required('Required') }),
		createAttachments: Yup.array().when('addLater', {
			is: false,
			then: Yup.array().min(1, 'Required').required('Required')
		})
	});

	return (
		<Formik
			validationSchema={formTwoValidationSchema}
			onSubmit={continueFormStepTwo}
			initialValues={PartThreeInitialValues}
			enableReinitialize={true}
		>
			{(formik) => {
				return (
					<Form translate="yes">
						<div className="form-group">
							<label htmlFor="certificationType" className="label">
								3. Please provide supporting Documents
							</label>

							<Field
								name="certificationType"
								children={() => {
									return (
										<SingleSelect
											name="certificationType"
											options={onboardingDocs}
											value={formik.values.certificationType}
											placeholder="Select form of documentation..."
											onChange={(val: string) => {
												formik.setFieldValue('certificationType', val);
												formik.setFieldValue('addLater', false);
												setCertificationFormData((prevState) => ({
													...prevState,
													policeCheck: {
														...prevState.policeCheck,
														certificationType: val
													}
												}));
											}}
										/>
									);
								}}
							/>

							<ErrorMessage name={`certificationType`}>
								{(error) => <div className="form-validation-error">{error}</div>}
							</ErrorMessage>
						</div>

						{!formik.values.addLater && formik.values.certificationType ? (
							<>
								<div className="form-group">
									<label htmlFor="date">Date of police check</label>

									<DateInput
										showDropdowns={true}
										excludeFuture={true}
										dateTimeString={formik.values.date || ''}
										onChange={(val: string) => {
											formik.setFieldValue('date', val);
											setCertificationFormData((prevState) => ({
												...prevState,
												policeCheck: {
													...prevState.policeCheck,
													date: val
												}
											}));
										}}
									/>

									<ErrorMessage name={`date`}>
										{(error) => <div className="form-validation-error">{error}</div>}
									</ErrorMessage>
								</div>

								<div className="form-group">
									<Field
										children={() => {
											return (
												<>
													<FileUpload
														name={`createAttachments`}
														placeholder="Choose file..."
														onChange={(val: string, files: any[]) => {
															formik.setFieldValue('createAttachments', val);
															setCertificationFormData((prevState) => ({
																...prevState,
																policeCheck: {
																	...prevState.policeCheck,
																	createAttachments: [val],
																	attachments: files
																}
															}));
														}}
														multiple={false}
														files={formik.values.attachments}
														label="Photo of identification"
													/>
												</>
											);
										}}
									/>

									<ErrorMessage name={`createAttachments`}>
										{(error) => <div className="form-validation-error">{error}</div>}
									</ErrorMessage>
								</div>
							</>
						) : null}

						<AddLaterRadio
							changeToUseExisting={existingCertifications && existingCertifications.length > 0}
							onChange={(val: boolean) => {
								formik.setFieldValue('addLater', val);
								formik.setFieldValue('certificationType', '');
							}}
							value={formik.values.addLater}
						/>
						<div className="general-divider"></div>

						{existingCertifications && existingCertifications.length > 0 ? (
							<ExistingCertification existingCertifications={existingCertifications} />
						) : null}

						<Button type="submit" className="btn btn-primary full-width">
							Continue
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};

export default PartThree;
